var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import Lenis from 'lenis';
import { useReducedMotion } from 'framer-motion';
const SmoothScrollContext = createContext({
    scrollTo: () => undefined,
});
export const useSmoothScroll = () => useContext(SmoothScrollContext);
const resolve = (target) => {
    if (typeof target === 'number')
        return target;
    if (typeof target === 'string') {
        try {
            return document.querySelector(target);
        }
        catch (_a) {
            return null;
        }
    }
    return target;
};
export function SmoothScroll({ children }) {
    const lenisRef = useRef(null);
    const reduced = useReducedMotion();
    useEffect(() => {
        if (reduced || !window.matchMedia('(pointer: fine)').matches)
            return undefined;
        const lenis = new Lenis({ autoRaf: true, lerp: 0.1, wheelMultiplier: 0.95 });
        lenisRef.current = lenis;
        return () => {
            lenis.destroy();
            lenisRef.current = null;
        };
    }, [reduced]);
    const scrollTo = useCallback((target, opts = {}) => {
        var _a, _b;
        const resolved = resolve(target);
        if (resolved === null)
            return;
        const offset = (_a = opts.offset) !== null && _a !== void 0 ? _a : 0;
        if (lenisRef.current) {
            lenisRef.current.scrollTo(resolved, {
                offset,
                immediate: opts.immediate,
                duration: (_b = opts.duration) !== null && _b !== void 0 ? _b : 1.5,
                easing: (t) => 1 - Math.pow(1 - t, 4),
            });
            return;
        }
        const top = typeof resolved === 'number'
            ? resolved
            : resolved.getBoundingClientRect().top + window.scrollY + offset;
        window.scrollTo({ top, behavior: reduced || opts.immediate ? 'auto' : 'smooth' });
    }, [reduced]);
    // Honour a deep link like /ai-interview#flow once the lazy sections exist:
    // retry briefly, since the target may live in a chunk that's still loading.
    useEffect(() => {
        const { hash } = window.location;
        if (!hash || hash.length < 2)
            return undefined;
        let tries = 0;
        let id = 0;
        const attempt = () => {
            if (resolve(hash) !== null) {
                // One more frame so the lazy chunk's layout settles before measuring.
                id = window.setTimeout(() => scrollTo(hash, { immediate: true }), 120);
                return;
            }
            if (++tries < 25)
                id = window.setTimeout(attempt, 150);
        };
        id = window.setTimeout(attempt, 250);
        return () => window.clearTimeout(id);
    }, [scrollTo]);
    const api = useMemo(() => ({ scrollTo }), [scrollTo]);
    return _jsx(SmoothScrollContext.Provider, { value: api, children: children });
}
/** In-page anchor that routes through the smooth-scroll instance. */
export function AnchorLink(_a) {
    var { href, children, className, onNavigate } = _a, rest = __rest(_a, ["href", "children", "className", "onNavigate"]);
    const { scrollTo } = useSmoothScroll();
    return (_jsx("a", Object.assign({ href: href, className: className, onClick: (e) => {
            if (!href.startsWith('#'))
                return;
            e.preventDefault();
            onNavigate === null || onNavigate === void 0 ? void 0 : onNavigate();
            scrollTo(href);
            window.history.replaceState(null, '', href);
            // Move focus for keyboard and screen-reader users without a jump.
            const el = document.querySelector(href);
            if (el) {
                if (!el.hasAttribute('tabindex'))
                    el.setAttribute('tabindex', '-1');
                el.focus({ preventScroll: true });
            }
        } }, rest, { children: children })));
}
